<!--
  @component SignInScreen — the consumer entry-point.
  tier:      screen (account-level — only renders when signed out;
             ConsumerRoot's auth guard bounces signed-in users
             from `/` to `/projects`).
  effects:   IdentityIO.signIn(email) — sends a one-time magic link.
-->
<script lang="ts">
  import { useIdentityIO } from "./IdentityIO.svelte";

  const identity = useIdentityIO();

  let email = $state("");
  let pending = $state(false);
  let error: string | null = $state(null);

  // ── Resend cooldown state ──────────────────────────────────────
  const RESEND_COOLDOWN_SECONDS = 30;
  let resendCooldown = $state(0);
  let resendTimer: ReturnType<typeof setInterval> | null = null;
  let justResent = $state(false);
  let mode = $state<"link" | "password">("link");
  let password = $state("");

  function startCooldown(): void {
    resendCooldown = RESEND_COOLDOWN_SECONDS;
    if (resendTimer) clearInterval(resendTimer);
    resendTimer = setInterval(() => {
      resendCooldown = Math.max(0, resendCooldown - 1);
      if (resendCooldown === 0 && resendTimer) {
        clearInterval(resendTimer);
        resendTimer = null;
      }
    }, 1000);
  }

  async function submit(): Promise<void> {
    if (!email) {
      error = "Enter your email address to continue.";
      return;
    }
    pending = true;
    error = null;
    try {
      await identity.signIn(email);
      startCooldown();
    } catch (e) {
      error = e instanceof Error ? e.message : "Sign-in failed. Please try again.";
    } finally {
      pending = false;
    }
  }

  async function submitPassword(): Promise<void> {
    if (!email || !password) {
      error = "Enter your email and password to continue.";
      return;
    }
    pending = true;
    error = null;
    try {
      await identity.signInWithPassword(email, password);
    } catch (e) {
      error = e instanceof Error ? e.message : "Sign-in failed. Please try again.";
    } finally {
      pending = false;
    }
  }

  async function resend(): Promise<void> {
    if (resendCooldown > 0 || pending) return;
    pending = true;
    error = null;
    try {
      await identity.signIn(pendingEmail);
      startCooldown();
      justResent = true;
      setTimeout(() => { justResent = false; }, 4000);
    } catch (e) {
      error = e instanceof Error ? e.message : "Couldn't resend the link. Please try again.";
    } finally {
      pending = false;
    }
  }

  function useDifferentEmail(): void {
    identity.cancelSignIn();
    if (resendTimer) clearInterval(resendTimer);
    resendCooldown = 0;
    error = null;
    justResent = false;
  }

  function onKey(e: KeyboardEvent): void {
    if (e.key === "Enter" && !pending) void submit();
  }

  const showPending = $derived(identity.signInPending);
  const pendingEmail = $derived(identity.signInPendingEmail ?? email);
</script>

<div class="screen">
  <div class="card">
    {#if !showPending}
      <header>
        <h1>Sign in</h1>
        <p class="subtitle">
          {#if mode === "link"}
            Deploy to the Liquidefy network. We'll email you a one-time
            sign-in link — no password to remember.
          {:else}
            Sign in with your email and password.
          {/if}
        </p>
      </header>

      {#if mode === "link"}
        <form onsubmit={(e) => { e.preventDefault(); void submit(); }}>
          <label>
            <span>Email</span>
            <input
              type="email"
              autocomplete="email"
              placeholder="you@example.com"
              bind:value={email}
              onkeydown={onKey}
              disabled={pending}
              required
            />
          </label>
          {#if error}<p class="error">{error}</p>{/if}
          <button type="submit" class="primary" disabled={!email || pending}>
            {pending ? "Sending…" : "Send sign-in link"}
          </button>
        </form>
        <button type="button" class="link toggle" onclick={() => { mode = "password"; error = null; }}>
          Sign in with a password
        </button>
      {:else}
        <form onsubmit={(e) => { e.preventDefault(); void submitPassword(); }}>
          <label>
            <span>Email</span>
            <input
              type="email"
              autocomplete="email"
              placeholder="you@liquidefy.io"
              bind:value={email}
              disabled={pending}
              required
            />
          </label>
          <label>
            <span>Password</span>
            <input
              type="password"
              autocomplete="current-password"
              placeholder="••••••••"
              bind:value={password}
              disabled={pending}
              required
            />
          </label>
          {#if error}<p class="error">{error}</p>{/if}
          <button type="submit" class="primary" disabled={!email || !password || pending}>
            {pending ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <button type="button" class="link toggle" onclick={() => { mode = "link"; error = null; }}>
          Use a magic link instead
        </button>
      {/if}

      <footer>
        <p>
          {#if mode === "link"}
            In development, the sign-in link is also printed to your
            browser console — handy for testing without email.
          {:else}
            Password sign-in is for allowlisted accounts only.
          {/if}
        </p>
      </footer>
    {:else}
      <header>
        <h1>Check your email</h1>
      </header>
      <p class="pending-msg">
        We sent a one-time sign-in link to
        <strong>{pendingEmail}</strong>. Open it to finish signing in.
      </p>
      <p class="pending-tip">
        The link expires in 15 minutes and works only once. Check your
        spam folder if it hasn't arrived within a minute.
      </p>

      {#if justResent}
        <p class="resent-flash" role="status">Link resent — give it a moment.</p>
      {/if}
      {#if error}<p class="error">{error}</p>{/if}

      <div class="pending-actions">
        <button
          type="button"
          class="resend"
          onclick={() => void resend()}
          disabled={resendCooldown > 0 || pending}
        >
          {#if pending}Sending…
          {:else if resendCooldown > 0}Resend in {resendCooldown}s
          {:else}Resend link{/if}
        </button>
        <button
          type="button"
          class="link"
          onclick={useDifferentEmail}
          disabled={pending}
        >
          Use a different email
        </button>
      </div>

      <footer>
        <p>
          In development the link also appears in your browser console,
          so you can copy it without checking email.
        </p>
      </footer>
    {/if}
  </div>
</div>

<style>
  .screen {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    font-family: var(--type-font);
  }
  .card {
    width: 100%;
    max-width: 408px;
    padding: var(--space-8);
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-lg);
    backdrop-filter: var(--glass-blur);
    -webkit-backdrop-filter: var(--glass-blur);
    color: var(--text-primary);
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }
  header {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  h1 {
    margin: 0;
    font-size: 21px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--text-primary);
  }
  .subtitle {
    margin: 0;
    font-size: 13.5px;
    color: var(--text-secondary);
    line-height: 1.6;
  }
  .pending-msg {
    margin: 0;
    font-size: 14px;
    color: var(--text-secondary);
    line-height: 1.6;
  }
  .pending-msg strong {
    color: var(--text-primary);
    font-family: var(--type-mono);
    font-size: 13px;
    font-weight: 500;
  }
  .pending-tip {
    margin: 0;
    font-size: 12.5px;
    color: var(--text-tertiary);
    line-height: 1.6;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  label {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  label span {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-tertiary);
  }
  input {
    padding: 11px var(--space-3);
    border-radius: var(--radius-md);
    border: 0.5px solid var(--border-input);
    background: var(--surface-sunken);
    color: var(--text-primary);
    font: inherit;
    font-size: 14px;
    transition: border-color var(--ease-fast);
  }
  input::placeholder { color: var(--text-placeholder); }
  input:focus {
    outline: none;
    border-color: var(--border-focus);
  }
  .error {
    margin: 0;
    font-size: 12.5px;
    color: var(--error);
  }

  /* Primary CTA — neutral raised, the one prominent action. */
  .primary {
    appearance: none;
    background: var(--bg-active);
    color: var(--text-primary);
    border: 0.5px solid var(--border-strong);
    border-radius: var(--radius-md);
    padding: 0 var(--space-4);
    height: 40px;
    font: inherit;
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
    margin-top: var(--space-1);
    transition: background var(--ease-fast), border-color var(--ease-fast);
  }
  .primary:hover:not(:disabled) {
    background: var(--bg-selected);
    border-color: var(--border-focus);
  }
  .primary:disabled { opacity: 0.45; cursor: not-allowed; }

  footer p {
    margin: 0;
    font-size: 11.5px;
    color: var(--text-ghost);
    line-height: 1.6;
  }

  .pending-actions {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-top: var(--space-1);
  }
  .resend {
    appearance: none;
    background: transparent;
    color: var(--text-primary);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-md);
    padding: 8px var(--space-3);
    font: inherit;
    font-size: 12.5px;
    cursor: pointer;
    transition: background var(--ease-fast);
  }
  .resend:hover:not(:disabled) { background: var(--bg-hover); }
  .resend:disabled { opacity: 0.45; cursor: not-allowed; }
  .link {
    appearance: none;
    background: transparent;
    border: 0;
    color: var(--text-secondary);
    font: inherit;
    font-size: 12.5px;
    padding: 0;
    cursor: pointer;
    transition: color var(--ease-fast);
  }
  .link:hover:not(:disabled) { color: var(--text-primary); text-decoration: underline; }
  .link:disabled { opacity: 0.45; cursor: not-allowed; }
  .toggle { align-self: flex-start; margin-top: calc(-1 * var(--space-2)); }
  .resent-flash {
    margin: 0;
    padding: var(--space-2) var(--space-3);
    background: var(--success-subtle);
    border: 0.5px solid var(--success-subtle);
    border-radius: var(--radius-sm);
    font-size: 12.5px;
    color: var(--success);
  }
</style>
